/**
 * Рендер поля боя на PixiJS (C1): рисует `BattleSnapshot` спрайтами атласа.
 *
 * - Камера — изометрия 2:1 атласа, поле целиком в свободной части экрана (`iso.ts`).
 * - Плавность — интерполяция между тиками 30 Гц: `render(snapshot, alpha)` рисует между прошлым и этим тиком (`track.ts`).
 * - Порядок — по глубине (мировая `y`) и слою; один контейнер с сортировкой.
 * - Анимации — по манифесту: направление спрайта из движения (иначе `facing`), кадр по времени анимации.
 * - Производительность — спрайты из пулов, ничего не создаётся каждый кадр; все кадры — из одного-двух листов атласа
 *   (батч в один-два draw call), земля — одна закешированная текстура.
 * - Пост-обработка (`fx.ts`) — tilt-shift запечён в землю, виньетка и свечение — спрайтами, без фильтров в кадре.
 *
 * Оболочка (C2) владеет циклом: создаёт поле, на каждый кадр зовёт `render`, на смену размера — `resize`.
 */
import type { BattleSnapshot, Direction, EffectKind, HeroId, StatusKind } from "@newfungame/contracts";
import { FIXED_ONE } from "@newfungame/contracts";
import { Application, BitmapFont, BitmapText, Container, Sprite, Texture } from "pixi.js";
import { planDamageNumbers } from "./numbers";
import { animTicks, createAnimResolver, frameAt, formatDamage, HIT_FLASH_TICKS, hitTint } from "./anim";
import type { LoadedAtlas } from "./atlas";
import { mergeLoaded } from "./atlas-merge";
import { fountainCount, fountainPoint } from "./coins";
import { bakeGround, createFieldFx } from "./fx";
import { buildGround, layoutFence } from "./ground";
import {
  cloudDepth,
  depthOf,
  directionFromDelta,
  fitCamera,
  Layer,
  NO_INSETS,
  screenToWorld,
  toSpriteDirection,
  worldToScreen,
  type Camera,
  type Insets,
  type Point,
} from "./iso";
import { FIELD_LOOKS, lookWarp, type FieldLook } from "./scale";
import { backSwap, displayPos, onBack, ROOF_HEIGHT, ROOF_HERO, roofOffset, roofShare, type BackSwap } from "./roof";
import { HERO_TINT, introFx, introPose, type IntroFxSink, type IntroPlan } from "./intro";
import { Track } from "./track";

export interface FieldOptions {
  /** Место под панели оболочки, px CSS: поле вписывается в остаток. */
  insets?: Insets;
  /** Цвет за краем земли. */
  background?: string | number;
  /** Плотность пикселей (по умолчанию `min(devicePixelRatio, 2)`). */
  resolution?: number;
  /** За чем следить по размеру (по умолчанию `window`). */
  resizeTo?: Window | HTMLElement;
  /** Куда летят монеты (счётчик монет в шапке HUD), px CSS. По умолчанию — середина верха. */
  coinTarget?: (width: number, height: number) => Point;
  /** Касание поля: что под пальцем (зомби, платформа, штаб) и мировая точка. */
  onTap?: (hit: FieldHit) => void;
  /** Рука A/B-теста масштаба поля (`scale.ts`); по умолчанию — как было. */
  look?: FieldLook;
}

export type FieldHit = { world: Point } & (
  { kind: "zombie"; id: number } | { kind: "platform"; slot: number } | { kind: "hq" } | { kind: "ground" }
);

export interface FieldStats {
  /** Видимых спрайтов в слое юнитов и поверх. */
  sprites: number;
  /** Объектов в пулах (создано всего). */
  pooled: number;
  /** Среднее время `render` за последние 60 вызовов, мс. */
  renderMs: number;
}

/** Для e2e и отладки: экранные точки последнего кадра. */
export interface FieldDebug {
  hq: Point | null;
  zombies: Point[];
  heroes: Point[];
  /** Чем нарисована земля: `grass` или локация беты, чьи кадры уже в атласе (e2e ждёт её, пока атлас догружается). */
  ground: string;
}

export interface Field {
  readonly app: Application;
  /** Нарисовать снимок: `alpha` ∈ [0, 1] — доля времени от прошлого тика к этому. */
  render(snapshot: BattleSnapshot, alpha: number): void;
  /** Подогнать под новый размер (сам зовётся при изменении окна на следующем `render`). */
  resize(): void;
  setInsets(insets: Insets): void;
  /** Сменить руку A/B-теста масштаба (`scale.ts`): камера и размеры со следующего `render`. */
  setLook(look: FieldLook): void;
  camera(): Camera;
  worldToScreen(p: Point): Point;
  stats(): FieldStats;
  debug(): FieldDebug;
  /**
   * Дозагруженный атлас (чиби после первого кадра, MADR 0017): его анимации перекрывают текущие, спрайты берут новые
   * кадры со следующего `render`.
   */
  addAtlas(more: LoadedAtlas): void;
  /** Перекличка героев (`intro.ts`): поза и эффекты поверх снимка; `null` — снять. На бой не влияет. */
  setIntro(plan: IntroPlan | null): void;
  /** Над головой героя в последнем кадре, px экрана; `null` — герой не нарисован. */
  heroAnchor(heroId: HeroId): Point | null;
  /** Отладка/e2e: скрыть юнитов (штаб, герои, зомби), чтобы сравнить кадр с землёй. */
  setUnitsVisible(visible: boolean): void;
  destroy(): void;
}

/**
 * Экранная высота верха платформы и крыши штаба над землёй, в ширинах клетки модели (герой стоит на платформе, турель —
 * на штабе). Чиби-модели (ISSUE-86): высота меша в клетках × 0,612 (ортокамера под 30°: cos 30° / √2) — крыша 1,175,
 * верх открытой платформы 0,38, см. `tools/voxel/chibi/hq.json`, `platform.json`.
 */
export const MOUNT_HEIGHT = { platform: 0.23, hq: 0.72 } as const;

/** Тонировка базы-цели боя атаки (зомби / герои соперника) и героев-защитников. */
const ATTACK_BASE_TINT = { zombies: 0xff9d9d, heroes: 0xa9c4ff } as const;
const DEFENDER_TINT = 0xffc9c9;

const STATUS_TINT: Record<StatusKind, number> = {
  frozen: 0x9bd8ff,
  slowed: 0xc4d4ff,
  burning: 0xffb072,
  shocked: 0xfff27a,
};

/** Эффекты-лучи (`to` задан): цвет полосы. */
const BEAM_TINT: Partial<Record<EffectKind, number>> = {
  lightning: 0x9ee7ff,
  fire: 0xffa53d,
  ice: 0xcff4ff,
  hit: 0xfff3a3,
};

/** Искры попадания крупнее кадра атласа: иначе при отзуме боя выстрел почти не виден. */
const HIT_FX_SIZE = 1.8;

const NUMBER_FONT = "nfg-damage";
/** Насколько можно растянуть кадр самого подробного атласа на большом экране (дальше — мыло). */
const MAX_UPSCALE = 1.25;
/**
 * Общий отзум боя. #38 — 0,55 (поле целиком с запасом); #84 — ближе к макету арены: на телефоне штаб на полэкрана, края
 * поля уходят под HUD; на десктопе между панелями — поле целиком. Масштаб — камерой, атлас не трогаем.
 */
const BATTLE_ZOOM = { portrait: 0.85, landscape: 0.6 } as const;
/** Одновременно видимых цифр урона не больше. */
const MAX_NUMBERS = 12;
/** Доля жизни цифры, после которой она скрыта (короче живут). */
const NUMBER_LIFE = 0.7;
/** Тиков на проявление нового зомби (появляются на краю поля, не «из воздуха»). */
const FADE_IN_TICKS = 8;
/** Над всеми юнитами: эффекты переклички рисуются поверх героев. */
const INTRO_Z = 1e7;
/** Высота головы над ногами героя, клеток. */
const HEAD_HEIGHT = 1.55;
/** Высота середины фигуры над ногами, клеток (свечение и кольцо вокруг героя). */
const BODY_HEIGHT = 0.55;

class Pool<T extends Sprite | BitmapText> {
  private free: T[] = [];
  created = 0;
  constructor(
    private readonly parent: Container,
    private readonly make: () => T,
  ) {}
  get(): T {
    const o = this.free.pop() ?? this.create();
    o.visible = true;
    return o;
  }
  put(o: T): void {
    o.visible = false;
    this.free.push(o);
  }
  private create(): T {
    const o = this.make();
    this.created++;
    this.parent.addChild(o);
    return o;
  }
}

interface View {
  track: Track;
  sprite: Sprite;
  pool: Pool<Sprite>;
  stamp: number;
  dir: Direction;
  frameName: string;
  /** Полоса здоровья (зомби): фон и заливка. */
  barBg?: Sprite;
  barFg?: Sprite;
  /** Луч эффекта или текст цифры урона. */
  beam?: Sprite;
  text?: BitmapText;
  /** Зомби проявляется (появился во время боя). */
  fadeIn?: boolean;
  /** Зомби: прочность в прошлом кадре и кадр последнего попадания. */
  lastHp?: number;
  hitFrame?: number;
  /** Монета: остальные монеты фонтана (первая — `sprite`). */
  extra?: Sprite[];
}

export async function createField(
  canvas: HTMLCanvasElement,
  initialAtlas: LoadedAtlas,
  opts: FieldOptions = {},
): Promise<Field> {
  const app = new Application();
  await app.init({
    canvas,
    resizeTo: opts.resizeTo ?? window,
    background: opts.background ?? "#5a9e35",
    antialias: false,
    autoDensity: true,
    resolution: opts.resolution ?? Math.min(window.devicePixelRatio || 1, 2),
    preference: "webgl",
    autoStart: false,
    sharedTicker: false,
  });
  app.ticker.stop();

  BitmapFont.install({
    name: NUMBER_FONT,
    style: {
      fontFamily: "system-ui, Arial, sans-serif",
      fontSize: 36,
      fontWeight: "900",
      fill: "#ffffff",
      stroke: { color: "#1b1b1b", width: 6, join: "round" },
    },
    chars: "0123456789.KM",
    resolution: 1,
  });

  let atlas = initialAtlas;
  let anims = createAnimResolver(atlas.manifest.animations);
  // самый подробный атлас (chibi — 128 px на клетку, voxel — 96) задаёт предел увеличения
  let maxTilePixels = 0;
  const fitTilePixels = (): void => {
    maxTilePixels = atlas.manifest.tilePixels;
    for (const f of atlas.frames.values()) maxTilePixels = Math.max(maxTilePixels, 1 / f.unit);
  };
  fitTilePixels();
  const ground = new Container();
  const groundSprite = new Sprite();
  ground.addChild(groundSprite);
  const units = new Container({ sortableChildren: true });
  // паркан площади — полосами среди юнитов, у каждой своя глубина (ground.ts)
  const fence: Sprite[] = [];
  const overlay = new Container();
  const beams = new Container();
  const bars = new Container();
  const numbers = new Container();
  const flying = new Container();
  overlay.addChild(beams, bars, numbers, flying);
  const fx = createFieldFx();
  app.stage.addChild(ground, units, fx.glows, fx.vignette, overlay);

  const unitPool = new Pool(units, () => new Sprite());
  const beamPool = new Pool(beams, () => {
    const s = new Sprite(Texture.WHITE);
    s.anchor.set(0, 0.5);
    return s;
  });
  const barPool = new Pool(bars, () => {
    const s = new Sprite(Texture.WHITE);
    s.anchor.set(0, 0.5);
    return s;
  });
  const textPool = new Pool(numbers, () => {
    const t = new BitmapText({ text: "0", style: { fontFamily: NUMBER_FONT, fontSize: 18 } });
    t.anchor.set(0.5, 1);
    return t;
  });
  const coinPool = new Pool(flying, () => new Sprite());

  let insets = opts.insets ?? NO_INSETS;
  let look = opts.look ?? FIELD_LOOKS.a;
  let cam: Camera = { scale: 40, originX: 0, originY: 0 };
  let layoutKey = "";
  let stamp = 0;
  let lastFrame = -1;
  let cannonFiredAt = -1000;
  let firstFrame = -1;
  const tmp: Point = { x: 0, y: 0 };
  const coinFrom: Point = { x: 0, y: 0 };
  const renderTimes: number[] = [];
  const views = {
    zombies: new Map<number, View>(),
    heroes: new Map<number, View>(),
    defenders: new Map<number, View>(),
    projectiles: new Map<number, View>(),
    effects: new Map<number, View>(),
    coins: new Map<number, View>(),
    statics: new Map<string, View>(),
  };
  let lastSnapshot: BattleSnapshot | null = null;
  let intro: IntroPlan | null = null;
  /** Ноги героев в последнем кадре. */
  const heroFeet = new Map<HeroId, Point>();
  const introSprites: Sprite[] = [];
  const introBeams: Sprite[] = [];
  let introSpritesUsed = 0;
  let introBeamsUsed = 0;
  const dbg: FieldDebug = { hq: null, zombies: [], heroes: [], ground: "grass" };
  /** Обмен платформы за штабом с платформой Gunner в последнем кадре (`roof.ts`). */
  let swap: BackSwap | null = null;

  const unitScale = (unit: number): number => cam.scale * Math.SQRT2 * unit;
  /** Пикселей на клетку у штаба, платформ и героев: в руке Г они крупнее камеры (`scale.ts`). */
  const baseScale = (): number => cam.scale * look.base;
  const mountPx = (h: number): number => h * baseScale() * Math.SQRT2;

  function relayout(s: BattleSnapshot): void {
    const { width, height } = app.screen;
    const key = `${look.zoom}:${width}x${height}:${insets.top},${insets.right},${insets.bottom},${insets.left}:${s.field.width}x${s.field.depth}:${s.hq.pos.x},${s.hq.pos.y}:${s.wave.level?.location ?? ""}`;
    if (key === layoutKey) return;
    layoutKey = key;
    cam = fitCamera(s.field, width, height, {
      insets,
      maxScale: (maxTilePixels / Math.SQRT2) * MAX_UPSCALE,
      // по экрану, не по свободной части: на десктопе она между панелями тоже бывает выше, чем шире
      zoom: (width < height ? BATTLE_ZOOM.portrait : BATTLE_ZOOM.landscape) * look.zoom,
      warp: lookWarp(look, s.hq.pos),
    });
    const g = { atlas, anims, cam, field: s.field, hq: s.hq.pos, width, height, location: s.wave.level?.location };
    const raw = new Container();
    dbg.ground = buildGround(raw, g);
    const old = groundSprite.texture;
    groundSprite.texture = bakeGround(app.renderer, raw, width, height);
    raw.destroy({ children: true });
    if (old !== Texture.EMPTY) old.destroy(true);
    fx.layout(width, height);
    layoutFence(units, fence, g);
  }

  function acquire(map: Map<number, View> | Map<string, View>, id: number | string): View {
    const m = map as Map<number | string, View>;
    let v = m.get(id);
    if (!v) {
      v = { track: new Track(), sprite: unitPool.get(), pool: unitPool, stamp, dir: "s", frameName: "" };
      v.sprite.alpha = 1;
      v.sprite.tint = 0xffffff;
      v.sprite.rotation = 0;
      m.set(id, v);
    }
    v.stamp = stamp;
    return v;
  }

  function release(v: View): void {
    v.pool.put(v.sprite);
    if (v.barBg) barPool.put(v.barBg);
    if (v.barFg) barPool.put(v.barFg);
    if (v.beam) beamPool.put(v.beam);
    if (v.text) textPool.put(v.text);
    if (v.extra) for (const e of v.extra) coinPool.put(e);
  }

  function sweep(map: Map<number | string, View>): void {
    for (const [id, v] of map) {
      if (v.stamp === stamp) continue;
      release(v);
      map.delete(id);
    }
  }

  /** Поставить кадр анимации в спрайт; возвращает высоту спрайта над якорем, px (для полосы здоровья). */
  function show(
    v: View,
    entity: string,
    anim: string,
    dir: Direction,
    ticks: number,
    sx: number,
    sy: number,
    z: number,
    sizeMul = 1,
  ): number {
    const a = anims.get(entity, anim, dir);
    const sp = v.sprite;
    if (!a) {
      sp.visible = false;
      return 0;
    }
    const name = frameAt(a, ticks);
    const f = atlas.frames.get(name);
    if (!f) {
      sp.visible = false;
      return 0;
    }
    sp.visible = true;
    if (name !== v.frameName) {
      v.frameName = name;
      sp.texture = f.texture;
      sp.anchor.set(f.anchorX, f.anchorY);
    }
    const k = unitScale(f.unit) * sizeMul;
    sp.scale.set(k);
    sp.position.set(sx, sy);
    if (sp.zIndex !== z) sp.zIndex = z;
    return f.texture.frame.height * f.anchorY * k;
  }

  function render(s: BattleSnapshot, alpha: number): void {
    const t0 = performance.now();
    lastSnapshot = s;
    relayout(s);
    stamp++;
    fx.begin();
    const tick = s.frame !== lastFrame;
    if (firstFrame < 0) firstFrame = s.frame;
    lastFrame = s.frame;
    const a = Math.min(1, Math.max(0, alpha));
    dbg.zombies.length = 0;
    dbg.heroes.length = 0;

    // платформы и штаб; платформа за штабом — на месте платформы Gunner (`roof.ts`)
    // бой атаки: база — цель в центре, Gunner на её крыше и обмен платформ не нужны
    swap = s.attack ? null : backSwap(s.hq.pos, s.platforms, s.heroes.find((h) => h.heroId === ROOF_HERO)?.pos ?? null);
    for (const p of s.platforms) {
      const v = acquire(views.statics, `pl:${p.slot}`);
      const at = displayPos(p.pos, swap);
      worldToScreen(cam, at.x, at.y, tmp);
      show(v, "platform", p.unlocked ? "open" : "locked", "s", 0, tmp.x, tmp.y, depthOf(at.y, Layer.ground), look.base);
    }
    const hqv = acquire(views.statics, "hq");
    worldToScreen(cam, s.hq.pos.x, s.hq.pos.y, tmp);
    show(hqv, "hq", s.hq.look, "s", s.frame, tmp.x, tmp.y, depthOf(s.hq.pos.y, Layer.unit), look.base);
    // база-цель боя атаки: красноватая у зомби, холодная у чужих героев
    hqv.sprite.tint = s.attack
      ? s.attack.kind === "zombies"
        ? ATTACK_BASE_TINT.zombies
        : ATTACK_BASE_TINT.heroes
      : 0xffffff;
    dbg.hq = { x: tmp.x, y: tmp.y - mountPx(MOUNT_HEIGHT.hq) / 2 };
    const cv = acquire(views.statics, "cannon");
    if (s.hq.cannon.firing) cannonFiredAt = s.frame;
    const sinceShot = s.frame - cannonFiredAt + a;
    // турель стоит на крыше: у руин крыши нет (ISSUE-86)
    if (s.hq.look === "destroyed" || s.attack) cv.sprite.visible = false;
    else
      show(
        cv,
        "cannon",
        sinceShot < 8 ? "fire" : "idle",
        toSpriteDirection(s.hq.cannon.facing),
        sinceShot < 8 ? sinceShot : s.frame,
        tmp.x,
        tmp.y - mountPx(MOUNT_HEIGHT.hq),
        depthOf(s.hq.pos.y, Layer.mounted),
        look.base,
      );

    // герои на платформах; Gunner — на крыше штаба, только на экране (`roof.ts`)
    let roofShift: Point | null = null;
    let roofHero: Point | null = null;
    let backShift: Point | null = null;
    let backHero: HeroId | null = null;
    for (const h of s.heroes) {
      const v = acquire(views.heroes, h.id);
      if (tick || v.track.frame < 0) v.track.update(s.frame, h.pos.x, h.pos.y);
      worldToScreen(cam, v.track.x(a), v.track.y(a), tmp);
      let x = tmp.x;
      let y = tmp.y - mountPx(MOUNT_HEIGHT.platform);
      let z = depthOf(h.pos.y, Layer.mounted);
      if (h.heroId === ROOF_HERO && !s.attack) {
        const platform = { x: tmp.x, y: tmp.y };
        worldToScreen(cam, s.hq.pos.x, s.hq.pos.y, tmp);
        x = tmp.x;
        y = tmp.y - mountPx(ROOF_HEIGHT);
        z = depthOf(s.hq.pos.y, Layer.projectile);
        roofShift = roofOffset({ x, y }, { x: platform.x, y: platform.y - cam.scale * 0.35 });
        roofHero = h.pos;
      } else if (swap && onBack(h.pos, swap)) {
        const back = { x, y };
        worldToScreen(cam, swap.roof.x, swap.roof.y, tmp);
        x = tmp.x;
        y = tmp.y - mountPx(MOUNT_HEIGHT.platform);
        z = depthOf(swap.roof.y, Layer.mounted);
        backShift = roofOffset({ x, y }, back);
        backHero = h.heroId;
      }
      const feet = heroFeet.get(h.heroId) ?? { x, y };
      feet.x = x;
      feet.y = y;
      heroFeet.set(h.heroId, feet);
      // настоящий выстрел важнее позы; герой за штабом на время показа выходит вперёд
      const pose = intro && h.anim === "idle" ? introPose(intro, h.heroId, s.wave.tick + a) : null;
      if (pose) {
        if (!backShift && h.pos.x === s.hq.pos.x && h.pos.y > s.hq.pos.y) z = depthOf(s.hq.pos.y, Layer.effect);
        const dir = toSpriteDirection(h.facing);
        const bs = baseScale();
        show(v, `hero.${h.heroId}`, "skill", dir, pose.ticks, x, y - pose.hop * bs, z, look.base);
        fx.glow(x, y - bs * BODY_HEIGHT, bs * 2, HERO_TINT[h.heroId], pose.glow);
      } else show(v, `hero.${h.heroId}`, h.anim, toSpriteDirection(h.facing), h.animTick + a, x, y, z, look.base);
      dbg.heroes.push({ x, y });
    }

    // защитники-герои боя атаки (копии героев владельца): стоят на земле вокруг базы
    if (s.attack) {
      for (const d of s.attack.defenders) {
        const v = acquire(views.defenders, d.id);
        worldToScreen(cam, d.pos.x, d.pos.y, tmp);
        show(
          v,
          `hero.${d.heroId}`,
          d.anim,
          toSpriteDirection(d.facing),
          d.animTick + a,
          tmp.x,
          tmp.y,
          depthOf(d.pos.y, Layer.unit),
          look.base,
        );
        if (v.sprite.tint !== DEFENDER_TINT) v.sprite.tint = DEFENDER_TINT;
      }
    }

    // зомби и боссы
    for (const zs of s.zombies) {
      const fresh = !views.zombies.has(zs.id);
      const v = acquire(views.zombies, zs.id);
      if (fresh) v.fadeIn = firstFrame !== s.frame && zs.anim === "walk";
      if (tick || v.track.frame < 0) v.track.update(s.frame, zs.pos.x, zs.pos.y);
      const x = v.track.x(a);
      const y = v.track.y(a);
      // направление — из движения; стоит (атака, смерть) — из снимка
      const moved = zs.anim === "walk" ? directionFromDelta(v.track.dx, v.track.dy, 2) : null;
      v.dir = moved ?? zs.facing;
      worldToScreen(cam, x, y, tmp);
      const entity = zs.kind === "boss" ? `boss.${zs.bossId ?? "boss_butcher"}` : `zombie.${zs.kind}`;
      const height = show(
        v,
        entity,
        zs.anim,
        toSpriteDirection(v.dir),
        zs.animTick + a,
        tmp.x,
        tmp.y,
        depthOf(y),
        look.zombie,
      );
      const sp = v.sprite;
      // проявление — только у появившихся после первого кадра (на старте боя зомби уже стоят)
      const age = s.frame - v.track.bornFrame + a;
      sp.alpha = v.fadeIn && age < FADE_IN_TICKS ? age / FADE_IN_TICKS : 1;
      const status = zs.statuses[0];
      if (v.lastHp !== undefined && zs.hp < v.lastHp) v.hitFrame = s.frame;
      v.lastHp = zs.hp;
      const tint = hitTint(status ? STATUS_TINT[status] : 0xffffff, s.frame - (v.hitFrame ?? -HIT_FLASH_TICKS));
      if (sp.tint !== tint) sp.tint = tint;
      drawBar(
        v,
        zs.hp,
        zs.hpMax,
        zs.anim !== "die" && (zs.kind === "boss" ? zs.hp < zs.hpMax : zs.hp < zs.hpMax * 0.7),
        tmp.x,
        tmp.y - height - 3,
        zs.kind === "boss",
      );
      if (zs.anim !== "die") dbg.zombies.push({ x: tmp.x, y: tmp.y - height / 2 });
    }

    // снаряды
    for (const p of s.projectiles) {
      const v = acquire(views.projectiles, p.id);
      if (tick || v.track.frame < 0) v.track.update(s.frame, p.pos.x, p.pos.y);
      const x = v.track.x(a);
      const y = v.track.y(a);
      v.dir = directionFromDelta(p.to.x - p.from.x, p.to.y - p.from.y) ?? v.dir;
      worldToScreen(cam, x, y, tmp);
      const shift = p.owner === ROOF_HERO ? roofShift : p.owner === backHero ? backShift : null;
      const share = shift ? roofShare(p) : 0;
      // летят на уровне груди: приподняты над землёй на полклетки
      const px = tmp.x + (shift?.x ?? 0) * share;
      const py = tmp.y - cam.scale * 0.35 + (shift?.y ?? 0) * share;
      show(
        v,
        `projectile.${p.kind}`,
        "fly",
        toSpriteDirection(v.dir),
        0,
        px,
        py,
        depthOf(y, share > 0 ? Layer.effect : Layer.projectile),
      );
      fx.tracer(px, py, p.to.x - p.from.x, (p.from.y - p.to.y) / 2, p.kind, cam.scale);
    }

    const numberPlan = planDamageNumbers(s.effects, MAX_NUMBERS);
    // эффекты: кадр по возрасту; цифры урона — текстом; лучи (молния, огонь, лёд) — полосой от pos к to
    for (const e of s.effects) {
      const v = acquire(views.effects, e.id);
      if (v.track.frame < 0) v.track.update(s.frame, e.pos.x, e.pos.y);
      const age = s.frame - v.track.bornFrame + a;
      const life = Math.max(1, e.durationTicks);
      worldToScreen(cam, e.pos.x, e.pos.y, tmp);
      if (e.kind === "damageNumber" || e.kind === "critNumber") {
        v.sprite.visible = false;
        const n = numberPlan.get(e.id);
        const k = age / (life * NUMBER_LIFE);
        if (!n || k >= 1) {
          if (v.text) textPool.put(v.text);
          v.text = undefined;
        } else drawNumber(v, n.value, n.crit, k, tmp.x, tmp.y);
        continue;
      }
      let sx = tmp.x;
      let sy = tmp.y;
      if (roofShift && roofHero && e.kind === "muzzle" && e.pos.x === roofHero.x && e.pos.y === roofHero.y) {
        sx += roofShift.x;
        sy += roofShift.y;
      } else if (backShift && onBack(e.pos, swap)) {
        // выстрел и луч героя с платформы за штабом — с его платформы на экране
        sx += backShift.x;
        sy += backShift.y;
      }
      if (e.to) {
        const fx = sx;
        const fy = sy - cam.scale * 0.35;
        worldToScreen(cam, e.to.x, e.to.y, tmp);
        sx = tmp.x;
        sy = tmp.y;
        drawBeam(v, fx, fy, sx, sy - cam.scale * 0.35, BEAM_TINT[e.kind] ?? 0xffffff, 1 - age / life);
      }
      const size =
        e.kind === "explosion" && e.value > 0
          ? Math.max(1, (e.value * 2) / FIXED_ONE / 0.5) * 0.6
          : e.kind === "hit"
            ? HIT_FX_SIZE
            : 1;
      show(v, `fx.${e.kind}`, "play", "s", age, sx, sy, depthOf(e.to ? e.to.y : e.pos.y, Layer.effect), size);
      if (e.kind === "explosion" && v.sprite.visible)
        v.sprite.zIndex = cloudDepth(e.pos.y, v.sprite.width / 2, cam.scale);
      fx.effect(sx, sy, e.kind, 1 - age / life, cam.scale * (e.kind === "explosion" ? size : 1));
    }

    // монеты: фонтаном от убитого зомби, потом по очереди к счётчику монет (`coins.ts`)
    const { width, height } = app.screen;
    const target = opts.coinTarget?.(width, height) ?? { x: width / 2, y: 24 };
    const anim = anims.get("coin", "spin", "s");
    for (const c of s.coins) {
      const v = coinView(c.id, fountainCount(c.value));
      if (v.track.frame < 0) v.track.update(s.frame, c.pos.x, c.pos.y);
      const age = s.frame - v.track.bornFrame + a;
      const k = Math.min(1, age / Math.max(1, c.flightTicks));
      const from = worldToScreen(cam, c.pos.x, c.pos.y, coinFrom);
      const n = 1 + (v.extra?.length ?? 0);
      for (let i = 0; i < n; i++) {
        const sp = i === 0 ? v.sprite : v.extra![i - 1]!;
        // монеты фонтана крутятся вразнобой
        const f = anim && atlas.frames.get(frameAt(anim, age + i * 3));
        if (!f) continue;
        const scale = fountainPoint(k, c.id, i, n, from, target, cam.scale, tmp);
        sp.texture = f.texture;
        sp.anchor.set(f.anchorX, f.anchorY);
        sp.scale.set(unitScale(f.unit) * 1.1 * scale);
        sp.position.set(tmp.x, tmp.y);
        fx.coin(tmp.x, tmp.y - f.texture.frame.height * f.anchorY * unitScale(f.unit) * 0.5, cam.scale * scale);
      }
    }

    sweep(views.zombies);
    sweep(views.heroes);
    sweep(views.defenders);
    sweep(views.projectiles);
    sweep(views.effects);
    sweep(views.coins);
    sweep(views.statics as Map<number | string, View>);
    drawIntro(s, a);
    fx.end();

    app.renderer.render(app.stage);
    renderTimes.push(performance.now() - t0);
    if (renderTimes.length > 60) renderTimes.shift();
  }

  function coinView(id: number, count: number): View {
    let v = views.coins.get(id);
    if (!v) {
      v = { track: new Track(), sprite: coinPool.get(), pool: coinPool, stamp, dir: "s", frameName: "" };
      v.extra = Array.from({ length: count - 1 }, () => coinPool.get());
      views.coins.set(id, v);
    }
    v.stamp = stamp;
    return v;
  }

  function drawBar(v: View, hp: number, hpMax: number, visible: boolean, x: number, y: number, boss: boolean): void {
    if (!visible) {
      if (v.barBg) barPool.put(v.barBg);
      if (v.barFg) barPool.put(v.barFg);
      v.barBg = v.barFg = undefined;
      return;
    }
    const zs = cam.scale * look.zombie;
    const w = zs * (boss ? 1.1 : 0.55);
    const h = Math.max(2, zs * (boss ? 0.07 : 0.05));
    const bg = (v.barBg ??= barPool.get());
    const fg = (v.barFg ??= barPool.get());
    bg.tint = 0x1f1f1f;
    bg.position.set(x - w / 2 - 1, y);
    bg.width = w + 2;
    bg.height = h + 2;
    fg.tint = boss ? 0xb026ff : 0xe23b3b;
    fg.position.set(x - w / 2, y);
    fg.width = Math.max(0, (w * hp) / hpMax);
    fg.height = h;
  }

  /** Эффекты поз переклички; спрайты из пула юнитов, только пока идёт показ. */
  function drawIntro(s: BattleSnapshot, a: number): void {
    introSpritesUsed = 0;
    introBeamsUsed = 0;
    if (intro) {
      const tick = s.wave.tick + a;
      for (const b of intro.beats) {
        const age = tick - intro.startTick - b.start;
        const feet = heroFeet.get(b.heroId);
        if (feet && age >= 0 && age <= b.length) introFx(introSink(feet, b.heroId), b.heroId, age, b.length);
      }
    }
    for (let i = introSpritesUsed; i < introSprites.length; i++) introSprites[i]!.visible = false;
    for (let i = introBeamsUsed; i < introBeams.length; i++) introBeams[i]!.visible = false;
  }

  function introSprite(
    entity: string,
    anim: string,
    dir: Direction,
    ticks: number,
    x: number,
    y: number,
    k: number,
  ): void {
    const a = anims.get(entity, anim, dir);
    const f = a && atlas.frames.get(frameAt(a, ticks));
    if (!a || !f || (!a.loop && ticks >= animTicks(a))) return;
    const sp = introSprites[introSpritesUsed] ?? unitPool.get();
    introSprites[introSpritesUsed++] = sp;
    sp.visible = true;
    sp.texture = f.texture;
    sp.anchor.set(f.anchorX, f.anchorY);
    sp.scale.set(unitScale(f.unit) * look.base * k);
    sp.position.set(x, y);
    sp.zIndex = INTRO_Z;
  }

  function introSink(feet: Point, heroId: HeroId): IntroFxSink {
    const tint = HERO_TINT[heroId];
    const bs = baseScale();
    const px = (v: number) => feet.x + v * bs;
    const py = (v: number) => feet.y + v * bs;
    return {
      sprite: (kind, x, y, ticks, size) => {
        if (ticks >= 0) introSprite(`fx.${kind}`, "play", "s", ticks, px(x), py(y), size);
      },
      bomb: (x, y) => introSprite("projectile.bomb", "fly", "ne", 0, px(x), py(y), 1),
      tracer: (x, y, dx, dy) => fx.tracer(px(x), py(y), dx, dy, "bullet", bs),
      arc: (x0, y0, x1, y1, alpha) => {
        const b = introBeams[introBeamsUsed] ?? beamPool.get();
        introBeams[introBeamsUsed++] = b;
        b.visible = true;
        b.tint = tint;
        b.alpha = alpha;
        b.position.set(px(x0), py(y0));
        b.rotation = Math.atan2(y1 - y0, x1 - x0);
        b.width = Math.hypot(x1 - x0, y1 - y0) * bs;
        b.height = Math.max(2, bs * 0.08);
      },
      glow: (x, y, size, alpha) => fx.glow(px(x), py(y), size * bs, tint, alpha),
      ring: (x, y, radius, alpha) => fx.ring(px(x), py(y), radius * bs, tint, alpha),
    };
  }

  function drawBeam(v: View, x0: number, y0: number, x1: number, y1: number, tint: number, alpha: number): void {
    const b = (v.beam ??= beamPool.get());
    b.tint = tint;
    b.alpha = Math.max(0, Math.min(1, alpha));
    b.position.set(x0, y0);
    b.rotation = Math.atan2(y1 - y0, x1 - x0);
    b.width = Math.hypot(x1 - x0, y1 - y0);
    b.height = Math.max(2, cam.scale * 0.08);
  }

  function drawNumber(v: View, value: number, crit: boolean, k: number, x: number, y: number): void {
    const t = (v.text ??= textPool.get());
    const text = formatDamage(value);
    if (t.text !== text) t.text = text;
    const size = Math.max(9, cam.scale * (crit ? 0.45 : 0.34));
    t.tint = crit ? 0xffd23f : 0xffffff;
    t.scale.set((size / 18) * (crit && k < 0.15 ? 1 + (0.15 - k) * 3 : 1));
    // всплывает и гаснет в последней трети
    t.position.set(x, y - cam.scale * (0.9 + k * 0.7));
    t.alpha = k < 0.66 ? 1 : Math.max(0, (1 - k) / 0.34);
  }

  function pickTap(ev: PointerEvent): void {
    if (!opts.onTap || !lastSnapshot) return;
    const r = canvas.getBoundingClientRect();
    const w = screenToWorld(cam, ev.clientX - r.left, ev.clientY - r.top);
    const s = lastSnapshot;
    const near = (x: number, y: number, rad: number) => (x - w.x) ** 2 + (y - w.y) ** 2 <= rad * rad;
    let best: FieldHit = { kind: "ground", world: w };
    let bestD = Infinity;
    for (const z of s.zombies) {
      const d = (z.pos.x - w.x) ** 2 + (z.pos.y - w.y) ** 2;
      if (z.anim !== "die" && d < bestD && d <= (0.7 * FIXED_ONE) ** 2) {
        bestD = d;
        best = { kind: "zombie", id: z.id, world: w };
      }
    }
    if (best.kind === "ground") {
      for (const p of s.platforms) {
        const at = displayPos(p.pos, swap);
        if (near(at.x, at.y, 0.9 * FIXED_ONE)) best = { kind: "platform", slot: p.slot, world: w };
      }
      if (best.kind === "ground" && near(s.hq.pos.x, s.hq.pos.y, 1.2 * FIXED_ONE)) best = { kind: "hq", world: w };
    }
    opts.onTap(best);
  }
  canvas.addEventListener("pointerdown", pickTap);

  return {
    app,
    render,
    resize() {
      app.resize();
      layoutKey = "";
      if (lastSnapshot) render(lastSnapshot, 1);
    },
    setInsets(i) {
      insets = i;
      layoutKey = "";
    },
    setLook(l) {
      if (l === look) return;
      look = l;
      layoutKey = "";
      if (lastSnapshot) render(lastSnapshot, 1);
    },
    camera: () => cam,
    worldToScreen: (p) => worldToScreen(cam, p.x, p.y),
    stats: () => ({
      sprites:
        units.children.reduce((n, c) => n + (c.visible ? 1 : 0), 0) +
        overlay.children.reduce((n, l) => n + l.children.reduce((m, c) => m + (c.visible ? 1 : 0), 0), 0),
      pooled: unitPool.created + beamPool.created + barPool.created + textPool.created + coinPool.created,
      renderMs: renderTimes.reduce((x, y) => x + y, 0) / Math.max(1, renderTimes.length),
    }),
    debug: () => dbg,
    addAtlas(more) {
      atlas = mergeLoaded(atlas, more);
      anims = createAnimResolver(atlas.manifest.animations);
      fitTilePixels();
      layoutKey = "";
      // имена кадров у воксельного и чиби-атласа совпадают — сбросить, чтобы спрайт взял новую текстуру
      for (const map of Object.values(views)) for (const v of map.values()) v.frameName = "";
    },
    setIntro(plan) {
      intro = plan;
    },
    heroAnchor(heroId) {
      const feet = heroFeet.get(heroId);
      return feet ? { x: feet.x, y: feet.y - baseScale() * HEAD_HEIGHT } : null;
    },
    setUnitsVisible(visible) {
      units.visible = visible;
      overlay.visible = visible;
      fx.glows.visible = visible;
      if (lastSnapshot) render(lastSnapshot, 1);
    },
    destroy() {
      canvas.removeEventListener("pointerdown", pickTap);
      app.destroy({ removeView: false }, { children: true });
    },
  };
}
